/**
 * useUserCampaigns Hook
 *
 * Fetches a user's subscribed campaigns with Supabase Realtime for live
 * enable/disable toggling.  Also exposes subscribe and unsubscribe actions
 * that call the corresponding Firebase Cloud Functions.
 *
 * @param {string} userId - Firebase UID of the current user
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import {
  fetchUserCampaigns,
  toggleUserCampaign,
  deleteUserCampaign,
  subscribeToUserCampaigns,
  transformUserCampaignToCamelCase,
} from '../api/supabase/automations';

export function useUserCampaigns(userId) {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [togglingId, setTogglingId] = useState(null);
  const channelRef = useRef(null);
  const loadingRef = useRef(false);

  const load = useCallback(async ({ silent = false } = {}) => {
    if (!userId) return;
    if (loadingRef.current) return;
    loadingRef.current = true;
    if (!silent) { setLoading(true); setError(null); }
    try {
      const data = await fetchUserCampaigns(userId);
      setCampaigns((data || []).map(transformUserCampaignToCamelCase));
    } catch (err) {
      console.error('[useUserCampaigns] load error:', err);
      if (!silent) setError(err.message || 'Failed to load campaigns');
    } finally {
      loadingRef.current = false;
      if (!silent) setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!userId) return;

    channelRef.current = subscribeToUserCampaigns(userId, () => {
      load({ silent: true });
    });

    return () => {
      if (channelRef.current) {
        channelRef.current.unsubscribe();
        channelRef.current = null;
      }
    };
  }, [userId, load]);

  const subscribe = useCallback(
    async (templateId) => {
      const functions = getFunctions(undefined, 'us-east1');
      const fn = httpsCallable(functions, 'subscribeToCampaign');
      const result = await fn({ templateId, targetUserId: userId });
      loadingRef.current = false;
      await load({ silent: true });
      return result.data;
    },
    [load, userId]
  );

  const toggle = useCallback(async (userCampaignId, isEnabled) => {
    setTogglingId(userCampaignId);
    try {
      const data = await toggleUserCampaign(userCampaignId, isEnabled);
      const updated = transformUserCampaignToCamelCase(data);
      setCampaigns((prev) =>
        prev.map((c) =>
          c.id === userCampaignId ? { ...c, isEnabled: updated.isEnabled } : c
        )
      );
    } catch (err) {
      console.error('[useUserCampaigns] toggle error:', err);
      throw err;
    } finally {
      setTogglingId(null);
    }
  }, []);

  const unsubscribe = useCallback(
    async (userCampaignId) => {
      try {
        await deleteUserCampaign(userCampaignId);
        setCampaigns((prev) => prev.filter((c) => c.id !== userCampaignId));
      } catch (err) {
        console.error('[useUserCampaigns] unsubscribe error:', err);
        throw err;
      }
    },
    []
  );

  return {
    campaigns,
    loading,
    error,
    togglingId,
    reload: load,
    subscribe,
    toggle,
    unsubscribe,
  };
}
